import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Stethoscope, CheckCircle2, AlertTriangle, XCircle, RefreshCw, ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const STYLE = {
  ok: { icon: CheckCircle2, ring: "border-emerald-200 bg-emerald-50/60", text: "text-emerald-700", dot: "bg-emerald-500" },
  warn: { icon: AlertTriangle, ring: "border-amber-300 bg-amber-50/70", text: "text-amber-700", dot: "bg-amber-500" },
  blocked: { icon: XCircle, ring: "border-red-300 bg-red-50/70", text: "text-red-700", dot: "bg-red-500" },
};
const VERDICT = {
  ok: "from-emerald-500 to-emerald-400",
  warn: "from-amber-500 to-orange-400",
  blocked: "from-red-600 to-[#FF007A]",
};

/** Panel de autodiagnóstico del radar: explica por qué el comercio (no) recibe matches. */
export default function RadarDiagnosis({ refreshKey }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/merchants/me/radar/diagnosis"); setD(data); }
    catch { setD(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d && loading) return <div className="rounded-3xl border border-neutral-200 bg-white p-5 text-sm text-neutral-500" data-testid="radar-diagnosis-loading">Revisando tu radar…</div>;
  if (!d) return null;

  const problems = d.checks.filter((c) => c.status !== "ok");
  const sorted = [...d.checks].sort((a, b) => ({ blocked: 0, warn: 1, ok: 2 }[a.status] - { blocked: 0, warn: 1, ok: 2 }[b.status]));

  return (
    <div className="rounded-3xl border border-neutral-200 bg-white overflow-hidden" data-testid="radar-diagnosis">
      <div className={`bg-gradient-to-r ${VERDICT[d.verdict]} text-white p-5 flex flex-wrap items-center gap-4`}>
        <div className="w-12 h-12 rounded-2xl bg-white/20 grid place-items-center shrink-0"><Stethoscope className="w-6 h-6" /></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Autodiagnóstico del radar</div>
          <div className="font-heading font-extrabold text-lg sm:text-xl leading-tight" data-testid="radar-diagnosis-headline">{d.headline}</div>
          <div className="text-xs opacity-90 mt-1">{d.summary.matches_month} matches este mes · {d.summary.matches_7d} en 7 días · {d.summary.nearby_7d} usuarios cerca</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 text-[11px] font-bold">
            <span className="rounded-full bg-white/25 px-2 py-1" data-testid="radar-diagnosis-count-blocked">{d.summary.blocked} críticos</span>
            <span className="rounded-full bg-white/25 px-2 py-1">{d.summary.warn} avisos</span>
            <span className="rounded-full bg-white/25 px-2 py-1">{d.summary.ok} ok</span>
          </div>
          <button onClick={load} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center" aria-label="Volver a revisar" data-testid="radar-diagnosis-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={() => setOpen((v) => !v)} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center" aria-label="Mostrar detalle" data-testid="radar-diagnosis-toggle"><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>

      {open && (
        <div className="p-4 sm:p-5">
          {problems.length > 0 && (
            <p className="text-xs text-neutral-500 mb-3">Resuelve primero los puntos en <strong className="text-red-600">rojo</strong>: cualquiera de ellos, por sí solo, impide que un usuario reciba tus ofertas aunque esté frente a tu vitrina.</p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sorted.map((c, i) => {
              const s = STYLE[c.status]; const Icon = s.icon;
              return (
                <motion.div key={c.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className={`rounded-2xl border p-3.5 flex flex-col ${s.ring}`} data-testid={`radar-check-${c.key}`} data-status={c.status}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${s.text}`} />
                    <div className="min-w-0">
                      <div className={`font-bold text-sm leading-tight ${s.text}`}>{c.title}</div>
                      <p className="text-xs text-neutral-700 mt-1 leading-snug">{c.detail}</p>
                    </div>
                  </div>
                  {c.fix && (
                    <div className="mt-2.5 pt-2.5 border-t border-black/5 text-xs">
                      <span className="font-bold text-neutral-800">Solución: </span><span className="text-neutral-700">{c.fix}</span>
                      {c.link && <Link to={c.link} className={`mt-1.5 inline-flex items-center gap-1 font-bold ${s.text}`} data-testid={`radar-check-${c.key}-link`}>Ir a resolverlo <ArrowRight className="w-3 h-3" /></Link>}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
